import { useMemo } from 'react';

import cn from 'classnames';

import TickImg from '@publicImages/svg/tick.svg';

import type { TPreCheckoutLineItemProps, TPromotionAddonProps } from '@/features/promoBanners/types';

import { selectDefaultCurrencyCode } from '@/ducks/common/selectors';
import { getBarTabText } from '@/features/promo/lib/getBarTabText';
import { selectMerchandisingBannerDetails } from '@/features/promoBanners/selectors';
import { useAppSelector } from '@/store';

import styles from './PromoBox.module.scss';

const getDynamicBarTabValue = (duration: number, promotionAddons?: TPromotionAddonProps[]) =>
  promotionAddons?.find((addOn) => {
    const min = addOn?.min ?? Number.MIN_VALUE;
    const max = addOn?.max ?? Number.MAX_VALUE;
    return min <= duration && duration <= max;
  })?.name;

type Props = {
  className?: string;
  isPayInFullAvailable: boolean;
  voyageDuration: number;
};

const PrecheckoutPromoBox = ({ className, voyageDuration }: Props) => {
  const { preCheckoutBarTabText, preCheckoutEyebrowText, preCheckoutHeaderText } =
    useAppSelector(selectMerchandisingBannerDetails) || {};
  const currencyCode = useAppSelector(selectDefaultCurrencyCode) || 'USD';
  const { preCheckoutLineItems, promotionAddons } = useAppSelector(selectMerchandisingBannerDetails) ?? {};

  const barTabValue = useMemo(
    () => getDynamicBarTabValue(voyageDuration, promotionAddons),
    [currencyCode, promotionAddons, voyageDuration],
  );

  return (
    <div className={cn(styles.root, className)}>
      {!!preCheckoutEyebrowText && <div className={styles.eyebrow}>{preCheckoutEyebrowText}</div>}
      <div className={styles.titlesContainer}>
        {!!preCheckoutHeaderText && <div className={styles.heading}>{preCheckoutHeaderText}</div>}
        <div className={styles.title}>{getBarTabText(preCheckoutBarTabText, barTabValue)}</div>
      </div>
      {preCheckoutLineItems?.map((item: TPreCheckoutLineItemProps) => (
        <div className={styles.discount} key={item?.id}>
          <span className={styles.icon}>
            <img alt={item?.bulletItemIcon?.alt || 'BulletText'} src={item?.bulletItemIcon?.src || TickImg} />
          </span>
          <div className={styles.discountDesc}>{item?.bulletItemText}</div>
        </div>
      ))}
    </div>
  );
};

export default PrecheckoutPromoBox;
